import { t } from '@lingui/macro'
import { Text, ToggleTabs } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC } from 'react'

import { useLuneI18nLocale } from '../../hooks'
import { type DisplayMassUnit } from '../../utils/utils'

import EmissionPollutants from './EmissionPollutants'
import { EmissionsExplanationSectionProps } from './useExplanationSteps'
import WtwBreakdownExplanation from './WtwBreakdownExplanation'

export const EmissionsBreakdowns: FC<{
    emissions: EmissionsExplanationSectionProps['emissions']
    emissionsUnit: DisplayMassUnit
    pdfStructure?: boolean
    title?: string
    showTitle?: boolean
    smallTitle?: boolean
    // eslint-disable-next-line complexity
}> = ({
    emissions,
    emissionsUnit,
    pdfStructure,
    title = t`Emissions breakdown`,
    showTitle = true,
    smallTitle = false,
}) => {
    useLuneI18nLocale()

    const titleVariant = smallTitle ? 'body2-medium' : 'h6'

    return (
        <>
            {!pdfStructure && emissions.wtwDetails && emissions.pollutants && (
                <Box>
                    {showTitle && (
                        <Text variant={titleVariant} sx={{ mb: 4, display: 'block' }}>
                            {title}
                        </Text>
                    )}
                    <ToggleTabs>
                        <ToggleTabs.Tab label={t`By WTW state`} value="wtw">
                            <WtwBreakdownExplanation
                                wtwDetails={emissions.wtwDetails}
                                emissionsUnit={emissionsUnit}
                                sx={{ mt: 4 }}
                            />
                        </ToggleTabs.Tab>
                        <ToggleTabs.Tab label={t`By pollutant`} value="pollutant">
                            <EmissionPollutants
                                pollutants={emissions.pollutants}
                                emissionsUnit={emissionsUnit}
                                sx={{ mt: 4 }}
                            />
                        </ToggleTabs.Tab>
                    </ToggleTabs>
                </Box>
            )}
            {!pdfStructure && emissions.wtwDetails && !emissions.pollutants && (
                <Box>
                    {showTitle && (
                        <Text variant={titleVariant} sx={{ mb: 4 }}>
                            {title}
                        </Text>
                    )}
                    <WtwBreakdownExplanation
                        wtwDetails={emissions.wtwDetails}
                        emissionsUnit={emissionsUnit}
                    />
                </Box>
            )}
            {!pdfStructure && !emissions.wtwDetails && emissions.pollutants && (
                <Box>
                    {showTitle && (
                        <Text variant={titleVariant} sx={{ mb: 4 }}>
                            {title}
                        </Text>
                    )}
                    <EmissionPollutants
                        pollutants={emissions.pollutants}
                        emissionsUnit={emissionsUnit}
                    />
                </Box>
            )}
            {pdfStructure && (
                <>
                    {emissions.wtwDetails && (
                        <Box>
                            <Text variant={'h6'} sx={{ mb: 4 }}>
                                {t`Emissions breakdown`}
                            </Text>
                            <WtwBreakdownExplanation
                                wtwDetails={emissions.wtwDetails}
                                emissionsUnit={emissionsUnit}
                                sx={{ mt: 4 }}
                            />
                        </Box>
                    )}
                    {emissions.pollutants && (
                        <Box>
                            <Text variant={'h6'} sx={{ mb: 4 }}>
                                {t`Pollutants`}
                            </Text>
                            <EmissionPollutants
                                pollutants={emissions.pollutants}
                                emissionsUnit={emissionsUnit}
                            />
                        </Box>
                    )}
                </>
            )}
        </>
    )
}

export default EmissionsBreakdowns
